import { AccessPointType } from "../../apis/networkdashboard/graphql/graphql";
import { ApCountsByModel } from "../../apis/networkdashboard/rest/models";

export interface AccessPointSummary {
  brand: string;
  model: string;
  active: number | string;
  inactive: number | string;
  total: number | string;
}

/**
 * Build per-model access-point summaries. Prefers the live counts Influx
 * reports; falls back to counting the subscription's access points by their
 * CI status when Influx has no data for the location (the backend sends an
 * empty apCountsByModel in that case).
 */
export function summarizeAccessPoints(
  accessPoints: AccessPointType[],
  apCountsByModel: Record<string, ApCountsByModel>,
): AccessPointSummary[] {
  if (Object.keys(apCountsByModel).length > 0) {
    return summarizeFromInflux(accessPoints, apCountsByModel);
  }

  type AccessPointCounts = {
    [brand: string]: {
      [model: string]: {
        [status: string]: number;
        total: number;
      };
    };
  };

  const accessPointCounts: AccessPointCounts = {};

  accessPoints.forEach(({ brand: rawBrand, model: rawModel, ciStatus: rawStatus }) => {
    const brand = rawBrand ?? "Unknown";
    const model = rawModel ?? "Unknown";
    const status = rawStatus ?? "Unknown";

    accessPointCounts[brand] ??= {};
    accessPointCounts[brand][model] ??= { total: 0 };
    accessPointCounts[brand][model][status] ??= 0;

    accessPointCounts[brand][model][status] += 1;
    accessPointCounts[brand][model].total += 1;
  });

  return Object.entries(accessPointCounts).flatMap(([brand, models]) =>
    Object.entries(models).map(([model, counts]) => ({
      brand,
      model,
      active: counts["Active"] ?? "-",
      inactive: counts["Stopped"] ?? "-",
      total: counts.total ?? "-",
    })),
  );
}

/**
 * Build per-model summaries from the active/inactive/total counts Influx
 * reports, labelling each model with the brand and full model name taken from
 * the subscription's access-point list.
 *
 * Both sides are matched on the model's digit run: Influx keys models as
 * "735" (arubav2 tag) or "AP_630" (xiq field), while the subscription list
 * uses full names such as "AP-735". An Influx model without a match keeps its
 * raw key and an empty brand; the image fallback handles unknown brands.
 */
function summarizeFromInflux(
  accessPoints: AccessPointType[],
  apCountsByModel: Record<string, ApCountsByModel>,
): AccessPointSummary[] {
  const modelInfo = new Map<string, { brand: string; fullName: string }>();
  for (const { brand, model } of accessPoints) {
    if (!brand || !model) continue;
    const digits = model.match(/\d+/);
    if (digits && !modelInfo.has(digits[0])) {
      modelInfo.set(digits[0], { brand, fullName: model });
    }
  }

  return Object.entries(apCountsByModel).map(([influxModel, counts]) => {
    const info = modelInfo.get(influxModel.match(/\d+/)?.[0] ?? influxModel);
    return {
      brand: info?.brand ?? "",
      model: info?.fullName ?? influxModel,
      active: counts.active,
      inactive: counts.inactive,
      total: counts.total,
    };
  });
}
